import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { BaseStyles, ThemeProvider } from '@primer/react';

interface DropdownPortalProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  children: React.ReactNode;
}

export const DropdownPortal: React.FC<DropdownPortalProps> = ({
  isOpen,
  onClose,
  triggerRef,
  children,
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isPositioned, setIsPositioned] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && triggerRef.current && dropdownRef.current) {
      setIsPositioned(false);

      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.offsetHeight;
      const spaceAbove = rect.top;
      const shouldOpenUpward = spaceAbove > dropdownHeight;

      let scrollParent = triggerRef.current.parentElement;
      const scrollParents: Array<{ element: Element, scroll: { top: number, left: number } }> = [];

      while (scrollParent && scrollParent !== document.body) {
        const computedStyle = getComputedStyle(scrollParent);
        const isScrollable = ['auto', 'scroll'].includes(computedStyle.overflow) ||
          ['auto', 'scroll'].includes(computedStyle.overflowY) ||
          ['auto', 'scroll'].includes(computedStyle.overflowX);

        if (isScrollable && (scrollParent.scrollTop > 0 || scrollParent.scrollLeft > 0)) {
          scrollParents.push({
            element: scrollParent,
            scroll: {
              top: scrollParent.scrollTop,
              left: scrollParent.scrollLeft
            }
          });
        }
        scrollParent = scrollParent.parentElement;
      }

      const calculatedTop = shouldOpenUpward
        ? rect.top - dropdownHeight - 4
        : rect.bottom + 4;
      const calculatedLeft = rect.left;
      const calculatedWidth = rect.width;

      setPosition({
        top: calculatedTop,
        left: calculatedLeft,
        width: calculatedWidth,
      });

      setIsPositioned(true);

      setTimeout(() => {
        if (dropdownRef.current) {
          const finalRect = dropdownRef.current.getBoundingClientRect();

          const buttonRect = triggerRef.current?.getBoundingClientRect();
          if (buttonRect) {
            const isOverlapping = !(
              finalRect.bottom < buttonRect.top ||
              finalRect.top > buttonRect.bottom ||
              finalRect.right < buttonRect.left ||
              finalRect.left > buttonRect.right
            );
          }
        }
      }, 10);
    }
  }, [isOpen, triggerRef]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        const dropdown = document.getElementById("section-selector-dropdown");
        if (dropdown && !dropdown.contains(event.target as Node)) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen) return null;

  return createPortal(
    <ThemeProvider>
      <BaseStyles>
        <div
          ref={dropdownRef}
          id="section-selector-dropdown"
          className="fixed bg-white border border-gray-300 rounded-lg shadow-xl max-h-96 overflow-hidden"
          style={{
            top: position.top,
            left: position.left,
            width: position.width,
            zIndex: 99999,
            minWidth: "320px",
            visibility: isPositioned ? 'visible' : 'hidden',
          }}
        >
          {children}
        </div>
      </BaseStyles>
    </ThemeProvider>,
    document.body
  );
};